/**
 * Tinta dos chips da agenda.
 *
 * Os pastéis do Ofative levam tinta escura (#1C1C1C). Cores saturadas — cor do
 * cliente, marca #0074FF, atrasos — precisam de tinta clara, senão o título
 * some no preenchimento. A luminância relativa segue WCAG 2.x.
 */

export const CHIP_INK_DARK = "#1C1C1C";
export const CHIP_META_DARK = "#3F3F46";
export const CHIP_INK_LIGHT = "#F4F4F5";
export const CHIP_META_LIGHT = "#E4E4E7";

/** Abaixo deste Y o preenchimento é tratado como saturado e usa tinta clara. */
const LIGHT_INK_LUMINANCE = 0.4;

export function parseHexColor(input: string): [number, number, number] | null {
  const hex = input.trim();
  const short = /^#([0-9a-fA-F]{3})$/.exec(hex);
  if (short?.[1] !== undefined) {
    const chars = short[1].split("");
    const r = chars[0];
    const g = chars[1];
    const b = chars[2];
    if (r === undefined || g === undefined || b === undefined) return null;
    return [
      Number.parseInt(`${r}${r}`, 16),
      Number.parseInt(`${g}${g}`, 16),
      Number.parseInt(`${b}${b}`, 16),
    ];
  }
  const full = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (full?.[1] === undefined) {
    const rgb = /^rgba?\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)/.exec(hex);
    if (rgb?.[1] === undefined || rgb[2] === undefined || rgb[3] === undefined) return null;
    return [Number.parseInt(rgb[1], 10), Number.parseInt(rgb[2], 10), Number.parseInt(rgb[3], 10)];
  }
  const value = full[1];
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
}

function channelToLinear(channel: number): number {
  const srgb = channel / 255;
  return srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number | null {
  const rgb = parseHexColor(hex);
  if (rgb === null) return null;
  const [r, g, b] = rgb;
  return 0.2126 * channelToLinear(r) + 0.7152 * channelToLinear(g) + 0.0722 * channelToLinear(b);
}

export function contrastRatio(hexA: string, hexB: string): number | null {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  if (a === null || b === null) return null;
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

export interface ChipInk {
  ink: string;
  meta: string;
}

export function chipInkForFill(fill: string): ChipInk {
  const luminance = relativeLuminance(fill);
  if (luminance === null) {
    return { ink: CHIP_INK_DARK, meta: CHIP_META_DARK };
  }
  if (luminance < LIGHT_INK_LUMINANCE) {
    return { ink: CHIP_INK_LIGHT, meta: CHIP_META_LIGHT };
  }
  return { ink: CHIP_INK_DARK, meta: CHIP_META_DARK };
}

/** Abaixo disto o fill é saturado demais para o card Ofative e precisa de branco. */
const PASTEL_MIN_LUMINANCE = 0.74;

export function mixHex(a: string, b: string, amountOfB: number): string | null {
  const left = parseHexColor(a);
  const right = parseHexColor(b);
  if (left === null || right === null) return null;
  const t = Math.max(0, Math.min(1, amountOfB));
  const channel = (index: 0 | 1 | 2) => Math.round(left[index] * (1 - t) + right[index] * t);
  const hex = [channel(0), channel(1), channel(2)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
  return `#${hex}`;
}

/**
 * Aproxima um fill saturado (cor da Agenda, marca do cliente) dos labels pastel
 * amostrados no card events do Ofative (`#D0E5FF`, `#F9DCD3`, …).
 */
export function pastelizeChipFill(fill: string): string {
  const luminance = relativeLuminance(fill);
  if (luminance === null || luminance >= PASTEL_MIN_LUMINANCE) return fill;
  let amount = 0.62;
  let mixed = mixHex(fill, "#FFFFFF", amount) ?? fill;
  for (let step = 0; step < 6; step += 1) {
    const next = relativeLuminance(mixed);
    if (next !== null && next >= PASTEL_MIN_LUMINANCE) return mixed;
    amount = Math.min(0.88, amount + 0.08);
    mixed = mixHex(fill, "#FFFFFF", amount) ?? mixed;
  }
  return mixed;
}

export function chipCssVars(fill: string, _foreground?: string | null): Record<string, string> {
  const pastel = pastelizeChipFill(fill);
  const fromFill = chipInkForFill(pastel);
  return {
    "--dream-chip-color": pastel,
    "--dream-chip-ink": fromFill.ink,
    "--dream-chip-meta": fromFill.meta,
  };
}
